import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Users, Plus, Edit2, Trash2, School, BookOpen, Calendar, CheckCircle2, XCircle, Clock, UserCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import ExportButton from '../components/ExportButton';
import WeeklyAttendance from '../components/WeeklyAttendance';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCHOOLS = [
  { id: 'suubi_primary', label: 'Suubi Primary', icon: BookOpen, accent: 'bg-blue-100 text-blue-600' },
  { id: 'suubi_secondary', label: 'Suubi Secondary', icon: School, accent: 'bg-purple-100 text-purple-600' },
];

const STATUS_STYLES = {
  present: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  absent: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  late: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
};

const emptyForm = {
  name: '', school: 'suubi_primary', student_class: '',
  gender: '', parent_names: '', parent_contact: '', home_address: '', notes: '',
};

const StudentsPage = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'school_head']);
  const canMarkAttendance = hasRole(['admin', 'school_head', 'staff']);

  const [view, setView] = useState('directory'); // directory | attendance | stats
  const [activeSchool, setActiveSchool] = useState('suubi_primary');
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  // Attendance state
  const [attendanceDate, setAttendanceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [attendanceTerm, setAttendanceTerm] = useState(`Term 1 ${new Date().getFullYear()}`);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileStudent, setProfileStudent] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [editAttendance, setEditAttendance] = useState(null); // record being edited
  const [attEditForm, setAttEditForm] = useState({ date: '', status: 'present', term: '', notes: '' });
  const [deleteAttendanceTarget, setDeleteAttendanceTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [listRes, sumRes, aggRes] = await Promise.all([
        axios.get(`${API_URL}/api/personnel/students`),
        axios.get(`${API_URL}/api/personnel/students/summary`),
        axios.get(`${API_URL}/api/personnel/students/attendance-aggregate`).catch(() => ({ data: null })),
      ]);
      setStudents(listRes.data);
      setSummary(sumRes.data);
      setAggregate(aggRes.data);
    } catch (e) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAttendanceForDate = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/personnel/students/attendance?date=${attendanceDate}`);
      const map = {};
      res.data.forEach((a) => { map[a.student_id] = a.status; });
      setAttendanceMap(map);
    } catch (e) { toast.error('Failed to load attendance for the selected date'); }
  }, [attendanceDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (view === 'attendance') loadAttendanceForDate();
  }, [view, loadAttendanceForDate]);

  const markAttendance = async (student_id, status) => {
    try {
      await axios.post(`${API_URL}/api/personnel/students/attendance`, {
        student_id, date: attendanceDate, status, term: attendanceTerm,
      });
      setAttendanceMap(prev => ({ ...prev, [student_id]: status }));
      // refresh aggregate
      const aggRes = await axios.get(`${API_URL}/api/personnel/students/attendance-aggregate`);
      setAggregate(aggRes.data);
    } catch (e) {
      toast.error('Failed to record attendance');
    }
  };

  const loadProfileData = async (student) => {
    try {
      const [sumRes, listRes] = await Promise.all([
        axios.get(`${API_URL}/api/personnel/students/${student.id}/attendance-summary`),
        axios.get(`${API_URL}/api/personnel/students/attendance?student_id=${student.id}`),
      ]);
      setProfileData({ summary: sumRes.data, history: listRes.data });
    } catch (e) { toast.error('Failed to load attendance history'); }
  };

  const openProfile = async (student) => {
    setProfileStudent(student);
    setProfileOpen(true);
    loadProfileData(student);
  };

  const openEditAttendance = (record) => {
    setEditAttendance(record);
    setAttEditForm({
      date: record.date || '',
      status: record.status || 'present',
      term: record.term || '',
      notes: record.notes || '',
    });
  };

  const handleAttendanceUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/personnel/students/attendance/${editAttendance.id}`, attEditForm);
      toast.success('Attendance entry updated');
      setEditAttendance(null);
      if (profileStudent) loadProfileData(profileStudent);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update attendance');
    }
  };

  const handleAttendanceDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/personnel/students/attendance/${deleteAttendanceTarget.id}`);
      toast.success('Attendance entry deleted');
      setDeleteAttendanceTarget(null);
      if (profileStudent) loadProfileData(profileStudent);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete attendance');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, school: activeSchool });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...emptyForm, ...s });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/personnel/students/${editing.id}`, form);
        toast.success('Student updated');
      } else {
        await axios.post(`${API_URL}/api/personnel/students`, form);
        toast.success('Student added');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/personnel/students/${id}`);
      toast.success('Student deleted');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const filtered = students.filter((s) => s.school === activeSchool);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Students Directory</h2>
          <p className="text-muted-foreground">Manage students, attendance and class statistics</p>
        </div>
        <ExportButton path="exports/list/students" filenameBase="suubi_students" testId="students-export-btn" />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="students-summary">
        <Card className="card-hover">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10"><Users className="h-5 w-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-2xl font-heading font-bold">{summary?.total || 0}</p>
            </div>
          </CardContent>
        </Card>
        {SCHOOLS.map((sc) => {
          const Icon = sc.icon;
          return (
            <Card key={sc.id} className="card-hover">
              <CardContent className="p-5 flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${sc.accent}`}><Icon className="h-5 w-5" /></div>
                <div>
                  <p className="text-xs text-muted-foreground">{sc.label}</p>
                  <p className="text-2xl font-heading font-bold">{summary?.by_school?.[sc.id] || 0}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
        <Card className="card-hover">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-pink-100 text-pink-600">♀</div>
            <div>
              <p className="text-xs text-muted-foreground">Female</p>
              <p className="text-2xl font-heading font-bold">{summary?.by_gender?.female || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-sky-100 text-sky-600">♂</div>
            <div>
              <p className="text-xs text-muted-foreground">Male</p>
              <p className="text-2xl font-heading font-bold">{summary?.by_gender?.male || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <TabsList>
            <TabsTrigger value="directory" data-testid="students-tab-directory">Directory</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="students-tab-attendance">Attendance</TabsTrigger>
            <TabsTrigger value="weekly" data-testid="students-tab-weekly">Weekly (Mon-Fri)</TabsTrigger>
            <TabsTrigger value="stats" data-testid="students-tab-stats">Statistics</TabsTrigger>
          </TabsList>
          {canEdit && view === 'directory' && (
            <Button onClick={openCreate} data-testid="add-student-btn">
              <Plus className="mr-1 h-4 w-4" /> Add Student
            </Button>
          )}
        </div>

        {/* DIRECTORY VIEW */}
        <TabsContent value="directory" className="mt-6 space-y-4">
          <Tabs value={activeSchool} onValueChange={setActiveSchool}>
            <TabsList>
              {SCHOOLS.map((sc) => (
                <TabsTrigger key={sc.id} value={sc.id} data-testid={`tab-${sc.id}`}>{sc.label}</TabsTrigger>
              ))}
            </TabsList>
            {SCHOOLS.map((sc) => (
              <TabsContent key={sc.id} value={sc.id} className="mt-4">
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Class</TableHead>
                          <TableHead>Gender</TableHead>
                          <TableHead>Parent(s)</TableHead>
                          <TableHead>Parent Contact</TableHead>
                          <TableHead>Address</TableHead>
                          {canEdit && <TableHead className="w-28"></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loading ? (
                          <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
                        ) : filtered.length === 0 ? (
                          <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No students in {sc.label}</TableCell></TableRow>
                        ) : (
                          filtered.map((s) => (
                            <TableRow key={s.id} className="table-row-hover">
                              <TableCell>
                                <button className="font-medium text-left hover:underline" onClick={() => openProfile(s)} data-testid={`student-name-${s.id}`}>{s.name}</button>
                              </TableCell>
                              <TableCell><Badge variant="outline">{s.student_class}</Badge></TableCell>
                              <TableCell className="capitalize">{s.gender || '—'}</TableCell>
                              <TableCell>{s.parent_names || '—'}</TableCell>
                              <TableCell>{s.parent_contact || '—'}</TableCell>
                              <TableCell className="text-muted-foreground max-w-xs truncate">{s.home_address || '—'}</TableCell>
                              {canEdit && (
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1.5">
                                    <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-student-${s.id}`} className="h-8 px-2.5">
                                      <Edit2 className="h-3.5 w-3.5 mr-1" />
                                      Edit
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setDeleteTarget(s)} data-testid={`delete-student-${s.id}`} className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive">
                                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                                      Delete
                                    </Button>
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>

        {/* ATTENDANCE VIEW */}
        <TabsContent value="attendance" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Daily Attendance</CardTitle>
              <CardDescription>Mark each student present/absent/late for the selected date</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 items-end mb-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} data-testid="attendance-date-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Term</Label>
                  <Input value={attendanceTerm} onChange={(e) => setAttendanceTerm(e.target.value)} placeholder="e.g., Term 1 2026" data-testid="attendance-term-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">School</Label>
                  <Select value={activeSchool} onValueChange={setActiveSchool}>
                    <SelectTrigger className="w-56" data-testid="attendance-school-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SCHOOLS.map(sc => <SelectItem key={sc.id} value={sc.id}>{sc.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Current Status</TableHead>
                    {canMarkAttendance && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No students in this school</TableCell></TableRow>
                  ) : (
                    filtered.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell>{s.student_class}</TableCell>
                        <TableCell className="capitalize">{s.gender || '—'}</TableCell>
                        <TableCell>
                          {attendanceMap[s.id]
                            ? <Badge className={STATUS_STYLES[attendanceMap[s.id]]}>{attendanceMap[s.id]}</Badge>
                            : <span className="text-xs text-muted-foreground">Not marked</span>}
                        </TableCell>
                        {canMarkAttendance && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant={attendanceMap[s.id] === 'present' ? 'default' : 'outline'} className={attendanceMap[s.id] === 'present' ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => markAttendance(s.id, 'present')} data-testid={`mark-present-${s.id}`}>
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Present
                              </Button>
                              <Button size="sm" variant={attendanceMap[s.id] === 'absent' ? 'default' : 'outline'} className={attendanceMap[s.id] === 'absent' ? 'bg-red-600 hover:bg-red-700' : ''} onClick={() => markAttendance(s.id, 'absent')} data-testid={`mark-absent-${s.id}`}>
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Absent
                              </Button>
                              <Button size="sm" variant={attendanceMap[s.id] === 'late' ? 'default' : 'outline'} className={attendanceMap[s.id] === 'late' ? 'bg-amber-600 hover:bg-amber-700' : ''} onClick={() => markAttendance(s.id, 'late')}>
                                <Clock className="h-3.5 w-3.5 mr-1" /> Late
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WEEKLY (MON-FRI) VIEW */}
        <TabsContent value="weekly" className="mt-6 space-y-4">
          <WeeklyAttendance schools={SCHOOLS} />
        </TabsContent>

        {/* STATISTICS VIEW */}
        <TabsContent value="stats" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Termly Attendance Statistics</CardTitle>
              <CardDescription>Aggregated attendance by gender</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 rounded-xl bg-background border">
                  <p className="text-sm text-muted-foreground">Total Records</p>
                  <p className="text-3xl font-heading font-bold">{aggregate?.total_attendance_records || 0}</p>
                </div>
                <div className="p-4 rounded-xl bg-background border">
                  <p className="text-sm text-muted-foreground">Overall Attendance Rate</p>
                  <p className="text-3xl font-heading font-bold text-primary">{aggregate?.overall_attendance_rate || 0}%</p>
                  <Progress value={aggregate?.overall_attendance_rate || 0} className="mt-2 h-1.5" />
                </div>
                <div className="p-4 rounded-xl bg-background border">
                  <p className="text-sm text-muted-foreground">Total Students Tracked</p>
                  <p className="text-3xl font-heading font-bold">{aggregate?.total_students || 0}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {['female', 'male'].map((g) => {
                  const data = aggregate?.by_gender?.[g] || { present: 0, absent: 0, late: 0, total: 0, attendance_rate: 0 };
                  const icon = g === 'female' ? '♀' : '♂';
                  const color = g === 'female' ? 'pink' : 'sky';
                  return (
                    <Card key={g} className={`border-${color}-200`}>
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-2xl text-${color}-600`}>{icon}</span>
                            <span className="font-heading font-semibold capitalize">{g}</span>
                          </div>
                          <Badge variant="outline">{data.attendance_rate}% attendance</Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-center">
                          <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/10">
                            <p className="text-xs text-muted-foreground">Present</p>
                            <p className="font-bold text-green-600">{data.present}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/10">
                            <p className="text-xs text-muted-foreground">Absent</p>
                            <p className="font-bold text-red-600">{data.absent}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/10">
                            <p className="text-xs text-muted-foreground">Late</p>
                            <p className="font-bold text-amber-600">{data.late}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add / Edit Student Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm({ ...emptyForm }); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Student' : 'Add Student'}</DialogTitle>
            <DialogDescription>Record student and parent information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>School *</Label>
                <Select value={form.school} onValueChange={(v) => setForm({ ...form, school: v })}>
                  <SelectTrigger data-testid="student-school-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCHOOLS.map((sc) => <SelectItem key={sc.id} value={sc.id}>{sc.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Class *</Label>
                <Input value={form.student_class} onChange={(e) => setForm({ ...form, student_class: e.target.value })} placeholder="e.g., P4, S2" required data-testid="student-class-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="student-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="student-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Parent(s) Name</Label>
              <Input value={form.parent_names} onChange={(e) => setForm({ ...form, parent_names: e.target.value })} placeholder="e.g., John & Mary Doe" />
            </div>
            <div className="space-y-2">
              <Label>Parent Contact Information</Label>
              <Input value={form.parent_contact} onChange={(e) => setForm({ ...form, parent_contact: e.target.value })} placeholder="Phone / email" />
            </div>
            <div className="space-y-2">
              <Label>Home Address</Label>
              <Textarea value={form.home_address} onChange={(e) => setForm({ ...form, home_address: e.target.value })} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="student-submit-btn">{editing ? 'Update' : 'Add'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Student Profile / Attendance History */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{profileStudent?.name}</DialogTitle>
            <DialogDescription>
              {profileStudent?.student_class} · {profileStudent?.school?.replace(/_/g, ' ')} · {profileStudent?.gender || 'Gender not specified'}
            </DialogDescription>
          </DialogHeader>
          {profileData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg border text-center">
                  <p className="text-xs text-muted-foreground">Total Days</p>
                  <p className="text-xl font-bold">{profileData.summary.total_days}</p>
                </div>
                <div className="p-3 rounded-lg border text-center bg-green-50 dark:bg-green-900/10">
                  <p className="text-xs text-muted-foreground">Present</p>
                  <p className="text-xl font-bold text-green-600">{profileData.summary.present}</p>
                </div>
                <div className="p-3 rounded-lg border text-center bg-red-50 dark:bg-red-900/10">
                  <p className="text-xs text-muted-foreground">Absent</p>
                  <p className="text-xl font-bold text-red-600">{profileData.summary.absent}</p>
                </div>
                <div className="p-3 rounded-lg border text-center bg-primary/5">
                  <p className="text-xs text-muted-foreground">Rate</p>
                  <p className="text-xl font-bold text-primary">{profileData.summary.attendance_rate}%</p>
                </div>
              </div>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Attendance History</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead>Term</TableHead>{canEdit && <TableHead className="text-right w-40"></TableHead>}</TableRow></TableHeader>
                    <TableBody>
                      {profileData.history.length === 0 ? (
                        <TableRow><TableCell colSpan={canEdit ? 4 : 3} className="text-center py-6 text-muted-foreground">No attendance history</TableCell></TableRow>
                      ) : profileData.history.map((h) => (
                        <TableRow key={h.id} data-testid={`attendance-row-${h.id}`}>
                          <TableCell>{h.date}</TableCell>
                          <TableCell><Badge className={STATUS_STYLES[h.status]}>{h.status}</Badge></TableCell>
                          <TableCell className="text-muted-foreground">{h.term || '—'}</TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openEditAttendance(h)} data-testid={`edit-attendance-${h.id}`}>
                                  <Edit2 className="h-3 w-3 mr-1" /> Edit
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 px-2 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteAttendanceTarget(h)} data-testid={`delete-attendance-${h.id}`}>
                                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit attendance entry */}
      {editAttendance && (
        <Dialog open onOpenChange={(o) => { if (!o) setEditAttendance(null); }}>
          <DialogContent className="max-w-sm" data-testid="edit-attendance-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit attendance entry</DialogTitle>
              <DialogDescription>{profileStudent?.name} — originally {editAttendance.date}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAttendanceUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={attEditForm.date} onChange={(e) => setAttEditForm({ ...attEditForm, date: e.target.value })} required data-testid="edit-attendance-date" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={attEditForm.status} onValueChange={(v) => setAttEditForm({ ...attEditForm, status: v })}>
                  <SelectTrigger data-testid="edit-attendance-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Term</Label>
                <Input value={attEditForm.term} onChange={(e) => setAttEditForm({ ...attEditForm, term: e.target.value })} placeholder="e.g., Term 1 2026" data-testid="edit-attendance-term" />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input value={attEditForm.notes} onChange={(e) => setAttEditForm({ ...attEditForm, notes: e.target.value })} placeholder="Optional notes" data-testid="edit-attendance-notes" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditAttendance(null)}>Cancel</Button>
                <Button type="submit" data-testid="edit-attendance-save">Save Changes</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete attendance entry confirm */}
      <AlertDialog open={!!deleteAttendanceTarget} onOpenChange={(o) => { if (!o) setDeleteAttendanceTarget(null); }}>
        <AlertDialogContent data-testid="delete-attendance-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete attendance entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteAttendanceTarget ? (
                <>Remove the <strong>{deleteAttendanceTarget.status}</strong> record for <strong>{deleteAttendanceTarget.date}</strong>? This cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAttendanceDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-attendance-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-student-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete student?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>Are you sure you want to delete <strong>{deleteTarget.first_name} {deleteTarget.last_name}</strong> ({deleteTarget.student_class})? This action cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDelete(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-student-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StudentsPage;
